// packages/shared/src/registry.ts
// Canonical token registry + helpers + pool registry (central source of truth)
import { z } from "zod";
/** Normalize to lowercase 0x…40 */
export const normalizeAddress = (addr) => addr.toLowerCase();
export const normalizeSymbol = (sym) => sym.toUpperCase();
/** Canonical token IDs we support in pricing & analytics (all-caps for consistency) */
export const TokenId = z.enum(["WETH", "USDC", "AERO", "CBBTC", "MSUSD", "KVCM", "ETH"]);
/** Base mainnet token registry (canonical) - defined after POOLS */
let TOKENS;
/** Fast symbol/address lookup tables - initialized after TOKENS */
let TOKENS_BY_SYMBOL;
let TOKENS_BY_ADDRESS;
/** Lookups */
export const getTokenBySymbol = (symbol) => TOKENS_BY_SYMBOL[symbol];
export const getTokenByAddress = (address) => TOKENS_BY_ADDRESS[normalizeAddress(address)];
// export function getTokenByAddress(addr?: string | null): TokenMeta | undefined {
//   if (!addr) return undefined;
//   return TOKENS_BY_ADDRESS[normalizeAddress(addr)];
// }
export const requireTokenBySymbol = (symbol) => {
    const t = getTokenBySymbol(symbol);
    if (!t)
        throw new Error(`unknown_token_symbol: ${symbol}`);
    return t;
};
export const requireTokenByAddress = (address) => {
    const t = getTokenByAddress(address);
    if (!t)
        throw new Error(`unknown_token_address: ${normalizeAddress(address)}`);
    return t;
};
/** Utility */
export const getTokenIdByAddress = (address) => getTokenByAddress(address)?.symbol;
export const POOLS = {
    CL100_WETH_USDC: {
        name: "CL100 WETH/USDC",
        pool: normalizeAddress("0xb2cc224c1c9feE385f8ad6a55b4d94E92359DC59"),
        token0: normalizeAddress("0x4200000000000000000000000000000000000006"), // WETH
        token1: normalizeAddress("0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"), // USDC
        kind: "slipstream",
        gauge: normalizeAddress("0xF33a96b5932D9E9B9A0eDA447AbD8C9d48d2e0c8"),
    },
    CL100_USDC_cbBTC: {
        name: "CL100 USDC/cbBTC",
        pool: normalizeAddress("0x4e962BB3889Bf030368F56810A9c96B83CB3E778"),
        token0: normalizeAddress("0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"), // USDC
        token1: normalizeAddress("0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf"), // CBBTC
        kind: "slipstream",
        gauge: normalizeAddress("0x6399ed6725cC163D019aA64FF55b22149D7179A8"),
    },
    CL100_WETH_cbBTC: {
        name: "CL100 WETH/cbBTC",
        pool: normalizeAddress("0x70aCDF2Ad0bf2402C957154f944c19Ef4e1cbAE1"),
        token0: normalizeAddress("0x4200000000000000000000000000000000000006"), // WETH
        token1: normalizeAddress("0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf"), // CBBTC
        kind: "slipstream",
        gauge: normalizeAddress("0x41b2126661C673C2beDd208cC72E85DC51a5320a"),
    },
    USDC_AERO: {
        name: "USDC/AERO",
        pool: normalizeAddress("0x6cDcb1C4A4D1C3C6d054b27AC5B77e89eAFb971d"),
        token0: normalizeAddress("0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"), // USDC
        token1: normalizeAddress("0x940181a94A35A4569E4529A3CDfB74e38FD98631"), // AERO
        kind: "basic",
        stable: false,
        gauge: normalizeAddress("0x4F09bAb2f0E15e2A078A227FE1537665F55b8360"),
    },
    msUSD_USDC: {
        name: "msUSD/USDC",
        pool: normalizeAddress("0xcEFC8B799a8EE5D9b312aeca73262645D664AaF7"),
        token0: normalizeAddress("0x526728DBc96689597F85ae4cd716d4f7fCcBAE9d"), // MSUSD
        token1: normalizeAddress("0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"), // USDC
        kind: "basic",
        stable: true,
        gauge: normalizeAddress("0xDBF852464fC906C744E52Dbd68C1b07dD33A922a"),
    },
    // CL2000_KVCM_USDC: Commented out - pool not registered in sugar contract yet
    // Waiting for sugar contract registry update before re-enabling
    // {
    //     name: "CL2000 KVCM/USDC",
    //     pool: normalizeAddress("0x5aAdAcac0deDeDd534990Ddc0Ea6565d104B48DE"),
    //     token0: normalizeAddress("0x00fBAC94Fec8D4089d3fe979F39454F48c71A65d"), // KVCM
    //     token1: normalizeAddress("0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"), // USDC
    //     kind: "slipstream",
    //     gauge: normalizeAddress("0x740546B5d6Ef109D33124b7E93B8ebf5f8f78d1B"),
    // },
    KVCM_WETH: {
        name: "KVCM/WETH",
        pool: normalizeAddress("0x5e710Fe3C079182a6a2be9cA3c200317d6C34A5D"),
        token0: normalizeAddress("0x00fBAC94Fec8D4089d3fe979F39454F48c71A65d"), // KVCM
        token1: normalizeAddress("0x4200000000000000000000000000000000000006"), // WETH
        kind: "basic",
        stable: false,
        gauge: normalizeAddress("0x3d4a5eAC08225e29EbbAeDE8721B23F57541Eb10"),
    },
    KVCM_USDC: {
        name: "KVCM/USDC",
        pool: normalizeAddress("0x5c0d76fab1822bdeb47308ed6028231761ed723e"),
        token0: normalizeAddress("0x00fBAC94Fec8D4089d3fe979F39454F48c71A65d"), // KVCM
        token1: normalizeAddress("0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"), // USDC
        kind: "basic",
        stable: false,
        gauge: normalizeAddress("0x57387e4639048B67C30C911a145368bC5B33fE3b"),
    },
};
const POOLS_BY_ADDRESS = Object.values(POOLS).reduce((m, p) => {
    m[p.pool] = p;
    return m;
}, {});
export function getPoolByAddress(addr) {
    return POOLS_BY_ADDRESS[normalizeAddress(addr)];
}
export function requirePoolByAddress(addr) {
    const p = getPoolByAddress(addr);
    if (!p)
        throw new Error(`unknown_pool_address: ${normalizeAddress(addr)}`);
    return p;
}
export function getPoolKindByAddress(addr) {
    return getPoolByAddress(addr)?.kind;
}
// Initialize TOKENS after POOLS is defined
TOKENS = {
    USDC: {
        symbol: "USDC",
        address: normalizeAddress("0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913"),
        decimals: 6,
        priceSource: { kind: "fixed", valueUsd: 1 },
        display_order: 1,
    },
    AERO: {
        symbol: "AERO",
        address: normalizeAddress("0x940181a94A35A4569E4529A3CDfB74e38FD98631"),
        decimals: 18,
        priceSource: {
            kind: "pool",
            poolType: "v2",
            poolAddress: POOLS.USDC_AERO.pool,
            base: "AERO",
            quote: "USDC",
        },
        display_order: 2,
    },
    CBBTC: {
        symbol: "CBBTC",
        address: normalizeAddress("0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf"),
        decimals: 8,
        priceSource: {
            kind: "pool",
            poolType: "slipstream",
            poolAddress: POOLS.CL100_USDC_cbBTC.pool,
            base: "CBBTC",
            quote: "USDC",
        },
        display_order: 3,
    },
    WETH: {
        symbol: "WETH",
        address: normalizeAddress("0x4200000000000000000000000000000000000006"),
        decimals: 18,
        priceSource: {
            kind: "pool",
            poolType: "slipstream",
            poolAddress: POOLS.CL100_WETH_USDC.pool,
            base: "WETH",
            quote: "USDC",
        },
        display_order: 4,
    },
    ETH: {
        symbol: "ETH",
        address: normalizeAddress("0x0000000000000000000000000000000000000000"), // Native ETH has no contract address
        decimals: 18,
        priceSource: {
            kind: "pool",
            poolType: "slipstream",
            poolAddress: POOLS.CL100_WETH_USDC.pool,
            base: "WETH", // Use WETH price for ETH
            quote: "USDC",
        },
        display_order: 5,
    },
    KVCM: {
        symbol: "KVCM",
        address: normalizeAddress("0x00fBAC94Fec8D4089d3fe979F39454F48c71A65d"),
        decimals: 18,
        priceSource: {
            kind: "pool",
            poolType: "v2", // Changed from slipstream to v2 (using KVCM_USDC basic pool instead of CL2000_KVCM_USDC)
            poolAddress: POOLS.KVCM_USDC.pool, // Using KVCM_USDC basic pool since CL2000_KVCM_USDC is not in sugar contract
            base: "KVCM",
            quote: "USDC",
        },
        display_order: 6,
    },
    MSUSD: {
        symbol: "MSUSD",
        address: normalizeAddress("0x526728DBc96689597F85ae4cd716d4f7fCcBAE9d"),
        decimals: 18,
        priceSource: {
            kind: "pool",
            poolType: "v2",
            poolAddress: POOLS.msUSD_USDC.pool,
            base: "MSUSD",
            quote: "USDC",
        },
        display_order: 7,
    },
};
// Initialize lookup tables
TOKENS_BY_SYMBOL = TOKENS;
TOKENS_BY_ADDRESS = Object.values(TOKENS).reduce((acc, t) => {
    acc[t.address] = t;
    return acc;
}, {});
// Export TOKENS for use in other modules
export { TOKENS };
